import { type ReactNode, Suspense } from "react";
import { Provider } from "react-redux";
import { QueryClientProvider } from "@tanstack/react-query";
import type { AppStore } from "~/app/store";
import { theme } from "~/app/theme";
import { GlobalStyles as MuiGlobalStyles, ThemeProvider } from "@schaeffler-commerce/design-system";
import { queryClient } from "~/app/queryClient";
import { AppBootstrapProvider } from "~/app/providers/AppBootstrapProvider";
import { SessionProvider } from "~/app/providers/SessionProvider";
import { MinicartProvider } from "~/app/providers/MinicartProvider";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { appGlobalStyles } from "~/common/styles/appGlobalStyles";
import { mountComponentRoot } from "~/utils/componentRootMount";
import { StorefrontShellRouterHost } from "~/app/shell/StorefrontShell";
import { ModalServiceRoot } from "~/common/utils/ModalService";

window.SCHA = window.SCHA || {};

let store: AppStore | null = null;

export function initializeComponentRegistry(nextStore: AppStore): void {
  store = nextStore;
}

/**
 * The storefront provider stack every host renders around the storefront tree
 * (task 0268): theme → global styles → localization → Redux → query client →
 * bootstrap → session → minicart. Requires `initializeComponentRegistry` to
 * have run (it owns the embedded store).
 */
export const StorefrontRoot = ({ children }: { children: ReactNode }) => {
  if (!store) {
    throw new Error("Component registry has not been initialized with a Redux store");
  }

  return (
    <Suspense fallback={null}>
      <ThemeProvider theme={theme}>
        <MuiGlobalStyles styles={appGlobalStyles} />
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Provider store={store}>
            <QueryClientProvider client={queryClient}>
              <AppBootstrapProvider>
                <SessionProvider>
                  <MinicartProvider>{children}</MinicartProvider>
                </SessionProvider>
              </AppBootstrapProvider>
            </QueryClientProvider>
          </Provider>
        </LocalizationProvider>
      </ThemeProvider>
    </Suspense>
  );
};

/**
 * The single owned storefront tree (task 0224): the provider stack around the
 * shell router host and the modal service host. The classic entry mounts this
 * component into `#root_schaShell`; the headless host renders it inside its
 * own document. This closes the ADR 0003 deferral: one root with one
 * Suspense gate replaces the per-island provider trees. `ModalServiceRoot`
 * stays inside `SessionProvider`'s keyed remount boundary (user ruling
 * 2026-09-28); the module-level modal registry carries open modals across
 * that remount.
 */
export const StorefrontAppTree = () => (
  <StorefrontRoot>
    <StorefrontShellRouterHost variant="standard" />
    <ModalServiceRoot />
  </StorefrontRoot>
);

/**
 * Mounts the single owned storefront tree into the classic JSP document
 * container. The retired contract — JSP island scripts queueing
 * `window.SCHA.createComponentAtId` calls until `markReactReady` — is gone;
 * the module entry calls this directly between `initializeLegacyRuntime`
 * and `finalizeLegacyBoot` (the former flush order). The container id is the
 * same `#root_schaShell` the JSP has always rendered.
 */
export function mountStorefrontApp(rootElement: HTMLElement | null = document.getElementById("root_schaShell")): void {
  if (!rootElement) {
    console.error("Could not find #root_schaShell to mount the storefront app");
    return;
  }

  mountComponentRoot(<StorefrontAppTree />, rootElement);
}
